import './supCalls.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import errorHandle from '../../utils/errorHandle';
import EmptyPage from '../../components/empty-page/emptyPage';
import Pagination from '../../components/pagination/Pagination';
import { listStaffCalls, listCallStaff, fetchCallRecording, updateStaffCall } from '../../api/supportAdmin';

// Trvto Calls — staff phone call tracking on the support side.
// Calls come from the staff Android app (call log + recordings).
const LIMIT = 20;
const DIRECTIONS = [
    ['', 'All calls'],
    ['outgoing', 'Outgoing'],
    ['incoming', 'Incoming'],
    ['missed', 'Missed'],
];
const OUTCOMES = [
    ['interested', 'Interested'],
    ['booked', 'Booked'],
    ['callback', 'Call back'],
    ['not_interested', 'Not interested'],
    ['no_answer', 'No answer'],
    ['other', 'Other'],
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES);

const todayStr = () => {
    const d = new Date(Date.now() + 5.5 * 3600e3);
    return d.toISOString().slice(0, 10);
};

const mins = (sec) => {
    if (!sec) return '0s';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const timePart = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};
const datePart = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const whenText = (iso) => (iso ? `${datePart(iso)}, ${timePart(iso)}` : '');

const personName = (call) => {
    if (call.match && call.match.name) return call.match.name;
    if (call.contactName) return call.contactName;
    return call.number || 'Unknown';
};

const kindOf = (call) => (call.match && call.match.kind ? call.match.kind : '');
const kindLabel = (call) => {
    const k = kindOf(call);
    return k ? k.charAt(0).toUpperCase() + k.slice(1) : '';
};
const initials = (call) => {
    const n = personName(call).trim();
    if (!n) return '?';
    if (/^[+\d]/.test(n)) return '#';
    return n.charAt(0).toUpperCase();
};

// direction glyph
const DirIcon = ({ dir }) => {
    if (dir === 'incoming') return <span className='scDirGlyph in'>↙</span>;
    if (dir === 'missed' || dir === 'rejected') return <span className='scDirGlyph miss'>✕</span>;
    return <span className='scDirGlyph out'>↗</span>;
};

const IconPhone = () => (
    <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z' />
    </svg>
);
const IconClock = () => (
    <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <circle cx='12' cy='12' r='9' />
        <path d='M12 7v5l3 2' />
    </svg>
);
const IconMissed = () => (
    <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <path d='M22 8l-6 6-4-4' /><path d='M22 14V8h-6' />
    </svg>
);
const IconRec = () => (
    <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
        <circle cx='12' cy='12' r='9' /><circle cx='12' cy='12' r='3.5' fill='currentColor' stroke='none' />
    </svg>
);

export default function SupCalls() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [canSeeAll, setCanSeeAll] = useState(false);
    const [staff, setStaff] = useState([]);
    const [selected, setSelected] = useState(null);

    const page = Number(searchParams.get('page')) || 1;
    const staffId = searchParams.get('staffId') || '';
    const direction = searchParams.get('direction') || '';
    const outcome = searchParams.get('outcome') || '';
    const recording = searchParams.get('recording') || '';
    const date = searchParams.get('date') || todayStr();
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [debounced, setDebounced] = useState(search);

    const update = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);
            Object.keys(params).forEach((k) => {
                if (params[k] === '' || params[k] === null) delete current[k];
                else current[k] = params[k];
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        listCallStaff()
            .then((res) => setStaff(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        let gone = false;
        setLoading(true);
        setData([]);
        listStaffCalls({ page, limit: LIMIT, staffId, direction, outcome, recording, from: date, to: date, search: debounced })
            .then((res) => {
                if (gone) return;
                setData(res.data.data || []);
                setStats(res.data.stats || {});
                setTotalCount(res.data.totalCount || 0);
                setCanSeeAll(res.data.canSeeAll);
            })
            .catch((e) => !gone && errorHandle(e))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [page, staffId, direction, outcome, recording, date, debounced]);

    const paginationClick = (number) => update({ page: number });

    return (
        <div className='supCalls'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}

            <div className='scHeader'>
                <h1 className='supportHeader scTitle'>Staff Calls</h1>
                <p className='scSubtitle'>Calls your team made to customers &amp; leads</p>
            </div>

            <div className='scStats'>
                <div className='scStatCard'>
                    <div className='scStatIcon blue'>
                        <IconPhone />
                    </div>
                    <div className='scStatText'>
                        <b>{stats.total || 0}</b>
                        <span>Total calls</span>
                    </div>
                </div>
                <div className='scStatCard'>
                    <div className='scStatIcon violet'>
                        <IconClock />
                    </div>
                    <div className='scStatText'>
                        <b>{mins(stats.talkSec || 0)}</b>
                        <span>Talk time</span>
                    </div>
                </div>
                <div className='scStatCard'>
                    <div className='scStatIcon red'>
                        <IconMissed />
                    </div>
                    <div className='scStatText'>
                        <b>{stats.missed || 0}</b>
                        <span>Missed</span>
                    </div>
                </div>
                <div className='scStatCard'>
                    <div className='scStatIcon green'>
                        <IconRec />
                    </div>
                    <div className='scStatText'>
                        <b>{stats.recorded || 0}</b>
                        <span>Recorded</span>
                    </div>
                </div>
            </div>

            <div className='scToolbar'>
                <input
                    type='date'
                    className='scInput'
                    value={date}
                    max={todayStr()}
                    onChange={(e) => update({ date: e.target.value, page: 1 })}
                />
                {canSeeAll && (
                    <select className='scInput' value={staffId} onChange={(e) => update({ staffId: e.target.value, page: 1 })}>
                        <option value=''>All staff</option>
                        {staff.map((s) => (
                            <option key={s._id} value={s._id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                )}
                <select className='scInput' value={direction} onChange={(e) => update({ direction: e.target.value, page: 1 })}>
                    {DIRECTIONS.map(([v, l]) => (
                        <option key={v} value={v}>
                            {l}
                        </option>
                    ))}
                </select>
                <select className='scInput' value={outcome} onChange={(e) => update({ outcome: e.target.value, page: 1 })}>
                    <option value=''>Any outcome</option>
                    {OUTCOMES.map(([v, l]) => (
                        <option key={v} value={v}>
                            {l}
                        </option>
                    ))}
                </select>
                <select className='scInput' value={recording} onChange={(e) => update({ recording: e.target.value, page: 1 })}>
                    <option value=''>Recording: any</option>
                    <option value='yes'>Has recording</option>
                    <option value='no'>No recording</option>
                </select>
                <div className='scSearchWrap'>
                    <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
                        <circle cx='11' cy='11' r='7' />
                        <path d='M21 21l-4.3-4.3' />
                    </svg>
                    <input
                        className='scInput scSearch'
                        placeholder='Search name, number or notes'
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            update({ search: e.target.value, page: 1 });
                        }}
                    />
                </div>
            </div>

            {!loading && data.length === 0 ? (
                <EmptyPage />
            ) : (
                <>
                    <div className='scCard'>
                        <div className='scTable'>
                            <div className='scTRow scTHead'>
                                <span>Contact</span>
                                <span>Staff</span>
                                <span>Direction</span>
                                <span>Time</span>
                                <span>Duration</span>
                                <span>Recording</span>
                                <span>Outcome</span>
                                <span>Lead info</span>
                                <span className='scColActions'>Actions</span>
                            </div>

                            {data.map((call) => (
                                <div className='scTRow' key={call._id} onClick={() => setSelected(call)}>
                                    <span className='scContact'>
                                        <div className={`scAvatar scKind-${kindOf(call) || 'none'}`}>{initials(call)}</div>
                                        <span className='scContactText'>
                                            <span className='scNameLine'>
                                                <b>{personName(call)}</b>
                                                {kindLabel(call) && (
                                                    <em className={`scPill scPill-${kindOf(call)}`}>{kindLabel(call)}</em>
                                                )}
                                            </span>
                                            <small>{call.contactName && call.match ? 'Saved contact' : 'Manual dial'}</small>
                                        </span>
                                    </span>

                                    <span className='scStaff'>
                                        <span className='scStaffAvatar'>{(call.staffName || '?').charAt(0).toUpperCase()}</span>
                                        <span className='scStaffName'>{call.staffName || '—'}</span>
                                    </span>

                                    <span className={`scDir scDir-${call.direction}`}>
                                        <DirIcon dir={call.direction} />
                                        <span className='scDirText'>{call.direction}</span>
                                    </span>

                                    <span className='scTime'>{whenText(call.startedAt)}</span>

                                    <span className='scDur'>{mins(call.durationSec)}</span>

                                    <span className='scRec'>
                                        {call.hasRecording ? (
                                            <em className='scTag green'>
                                                <span className='scDot' /> Recorded
                                            </em>
                                        ) : call.recordingMissing ? (
                                            <em className='scTag amber'>None</em>
                                        ) : (
                                            <em className='scTag grey'>Pending</em>
                                        )}
                                    </span>

                                    <span className='scOut'>
                                        {call.outcome ? (
                                            <em className={`scOutPill out-${call.outcome}`}>
                                                {OUTCOME_LABEL[call.outcome] || call.outcome}
                                            </em>
                                        ) : (
                                            <span className='scMutedDash'>—</span>
                                        )}
                                    </span>

                                    <span className='scLeadInfo'>
                                        {call.context && call.context.label ? (
                                            <span className='scLeadText'>{call.context.label}</span>
                                        ) : (
                                            <span className='scMutedDash'>—</span>
                                        )}
                                    </span>

                                    <span className='scColActions scActions' onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className={`scActBtn ${call.hasRecording ? 'play' : ''}`}
                                            title={call.hasRecording ? 'Play recording' : 'No recording'}
                                            disabled={!call.hasRecording}
                                            onClick={() => setSelected(call)}
                                        >
                                            ▶
                                        </button>
                                        <button className='scActBtn' title='Notes & outcome' onClick={() => setSelected(call)}>
                                            <svg viewBox='0 0 24 24' width='15' height='15' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                                                <path d='M4 5h16M4 10h16M4 15h10' />
                                            </svg>
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <Pagination
                        paginationClick={paginationClick}
                        numberOfRows={totalCount}
                        offSet={(page - 1) * LIMIT}
                        pageNumber={page}
                        limit={LIMIT}
                    />
                </>
            )}

            {selected && (
                <CallPopup
                    call={selected}
                    onClose={() => setSelected(null)}
                    onSaved={(c) => setData((d) => d.map((x) => (x._id === c._id ? { ...x, ...c } : x)))}
                />
            )}
        </div>
    );
}

function CallPopup({ call, onClose, onSaved }) {
    const [audioUrl, setAudioUrl] = useState('');
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState('');
    const [notes, setNotes] = useState(call.notes || '');
    const [outcome, setOutcome] = useState(call.outcome || '');
    const [saving, setSaving] = useState(false);
    const urlRef = useRef('');

    useEffect(() => {
        return () => {
            if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        };
    }, []);

    const loadAudio = () => {
        setAudioLoading(true);
        setAudioError('');
        fetchCallRecording(call._id)
            .then((res) => {
                const url = URL.createObjectURL(res.data);
                urlRef.current = url;
                setAudioUrl(url);
            })
            .catch(() => setAudioError("Couldn't load the recording"))
            .finally(() => setAudioLoading(false));
    };

    const save = () => {
        setSaving(true);
        updateStaffCall(call._id, { notes, outcome: outcome || null })
            .then(() => {
                onSaved({ _id: call._id, notes, outcome });
                onClose();
            })
            .catch((e) => errorHandle(e))
            .finally(() => setSaving(false));
    };

    return (
        <div className='supCallOverlay' onClick={onClose}>
            <div className='supCallPopup' onClick={(e) => e.stopPropagation()}>
                <div className='supCallPopHead'>
                    <div className='supCallPopWho'>
                        <div className={`supCallPopAvatar scKind-${kindOf(call) || 'none'}`}>{initials(call)}</div>
                        <div>
                            <div className='supCallPopName'>
                                <h3>{personName(call)}</h3>
                                {kindLabel(call) && <em className='supCallKind'>{kindLabel(call)}</em>}
                            </div>
                            <p>
                                {call.direction} · {whenText(call.startedAt)} · {mins(call.durationSec)}
                                {call.staffName ? ` · by ${call.staffName}` : ''}
                            </p>
                        </div>
                    </div>
                    <button className='supCallClose' onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className='supCallPopBody'>
                    <div className='supCallSection'>
                        <label>Recording</label>
                        {call.hasRecording ? (
                            audioUrl ? (
                                <audio controls src={audioUrl} className='supCallAudio' />
                            ) : audioLoading ? (
                                <span className='supCallMuted'>Loading…</span>
                            ) : audioError ? (
                                <span className='supCallMuted'>{audioError}</span>
                            ) : (
                                <button className='blueBtn' onClick={loadAudio}>
                                    ▶ Load recording
                                </button>
                            )
                        ) : (
                            <span className='supCallMuted'>
                                {call.recordingMissing ? 'No recording for this call.' : 'Not uploaded yet.'}
                            </span>
                        )}
                    </div>

                    {call.number && (
                        <div className='supCallSection'>
                            <label>Number</label>
                            <span>{call.number}</span>
                        </div>
                    )}

                    <div className='supCallSection'>
                        <label>Outcome</label>
                        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className='supCallInput'>
                            <option value=''>—</option>
                            {OUTCOMES.map(([v, l]) => (
                                <option key={v} value={v}>
                                    {l}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='supCallSection'>
                        <label>Notes — what the customer asked</label>
                        <textarea className='supCallNotes' rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>

                    {call.followUpAt && (
                        <div className='supCallSection'>
                            <label>Follow-up</label>
                            <span>{whenText(call.followUpAt)}</span>
                        </div>
                    )}
                </div>

                <div className='supCallPopFoot'>
                    <button className='supCallGhost' onClick={onClose}>
                        Cancel
                    </button>
                    <button className='blueBtn' onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
